import { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from 'State/AuthContext';
import { useHasAccess } from 'Routes/hooks/useHasAccess';
import { routeConfigV2, isWipOnly } from 'Routes/RouteConfigV2';
import NotAuthorized from 'Pages/NotAuthorized';

/**
 * EntitlementGuard
 *
 * A per-route permission gate. Wraps each route's element in ProtectedRoutesV2
 * and decides what to render based on the user's entitlements.
 *
 * Decision tree:
 *  1. No `resource` defined → open to any authenticated user (render element).
 *  2. User lacks required entitlement →
 *       a. If another route is accessible → <Navigate> to it synchronously
 *          (prevents the NotAuthorized flicker during role switch).
 *       b. Otherwise → render <NotAuthorized />.
 *  3. Route has `resolveElement` → call it with profileRole (role-based component swap).
 *  4. Otherwise → render children (the default element).
 *
 * Why the synchronous redirect (2a):
 *   On role switch, the entitlements store updates and the currently-mounted
 *   route re-renders against the new role. Without a synchronous redirect, this
 *   guard would briefly render <NotAuthorized /> until a post-render useEffect
 *   in ProtectedRoutesV2 navigated away — producing a visible ~1s flicker.
 *   Redirecting during render eliminates that gap.
 */
const EntitlementGuard = ({
  resource,
  requiredEntitlements = [],
  resolveElement,
  children,
}) => {
  const { profileRole } = useContext(AuthContext);
  const { hasAccess } = useHasAccess();
  const location = useLocation();

  // ── Permission check ──────────────────────────────────────────────────────
  if (resource && !hasAccess(resource, requiredEntitlements)) {
    // Try to redirect to the first route the current role *can* access so the
    // user never sees the NotAuthorized page during a role switch.
    const accessibleRoutes = routeConfigV2.filter(
      (r) => r.resource && hasAccess(r.resource, r.requiredEntitlements),
    );
    const firstAccessible =
      accessibleRoutes.find((r) => !isWipOnly(r.requiredEntitlements)) ??
      accessibleRoutes[0];
    if (firstAccessible && firstAccessible.path !== location.pathname) {
      return <Navigate to={firstAccessible.path} replace />;
    }
    return <NotAuthorized />;
  }

  // ── Role-based component swap ─────────────────────────────────────────────
  // e.g. online_user sees JobsLibraryOnline, everyone else sees JobsLibraryV2
  if (resolveElement) {
    return resolveElement(profileRole);
  }

  // ── Default element ───────────────────────────────────────────────────────
  return children;
};

export default EntitlementGuard;